/**
 * admitBundleHeaders
 *
 * The ONE place the "header follows its members" rule lives: after split and
 * column filters judge every row on its own cell value, any bundle with at
 * least one surviving member re-admits its header, in document order, so no
 * rendered member on a page is ever headerless. Consumed by `ArTableBody`
 * (between the column-filter narrowing and the PDF-selection narrowing), by
 * `resolveArTableVisibleRows` (between the same two, before
 * `resolveArTableBundleExportVisibility`) and by `createArTableExportSnapshot`
 * (between the split narrowing and the same resolver, above its own
 * selection narrowing). It must NOT be applied to the
 * PDF/XLSX ticked-selection narrowing: SYD-2279 deliberately orphans a member
 * whose header is unticked so it fails closed. A header absent from `rowIds`
 * (off-page across a pagination boundary) is never fabricated — that member
 * keeps failing closed exactly as `classifyArBundleRows` documents.
 */

import type { ArRowData, ArRowId } from '#types';
import type { DocEntry } from '../../../store/Store.types';

/**
 * Widens `admittedRowIds` (survivors of split ∧ column filters, a subsequence
 * of `rowIds`) with the header row of every admitted member. A new array is
 * built only when some header is actually admitted; otherwise the caller's own
 * `admittedRowIds` reference comes straight back, so a `useMemo` chain keyed on
 * it does not churn — callers that copy the result (`ArTableBody` wraps it in
 * `[...]`, since `SortableContext` and `arrayMove` reject a readonly array) get
 * a fresh array regardless. A candidate header is admitted only when it is a
 * real header row on this page — `rowsDocs` holds nothing else — and owns the
 * member's bundleId.
 */
function admitBundleHeaders(params: {
  rowIds: readonly ArRowId[];
  admittedRowIds: readonly ArRowId[];
  rowsDocs: ReadonlyMap<string, DocEntry<ArRowData>> | null;
}): readonly ArRowId[] {
  const { rowIds, admittedRowIds, rowsDocs } = params;
  if (!rowsDocs) return admittedRowIds;
  if (admittedRowIds.length === rowIds.length) return admittedRowIds;

  const admittedSet = new Set<ArRowId>(admittedRowIds);
  const headersToAdmit = new Set<ArRowId>();

  for (const rowId of admittedRowIds) {
    const data = rowsDocs.get(rowId)?.data;
    if (data?.bundleRole !== 'member' || !data.bundleId) continue;
    const headerRowId: ArRowId = data.bundleId;
    if (admittedSet.has(headerRowId) || headersToAdmit.has(headerRowId)) continue;
    const headerData = rowsDocs.get(headerRowId)?.data;
    if (headerData?.bundleRole !== 'header' || headerData.bundleId !== data.bundleId) continue;
    headersToAdmit.add(headerRowId);
  }

  if (headersToAdmit.size === 0) return admittedRowIds;

  return rowIds.filter((rowId) => admittedSet.has(rowId) || headersToAdmit.has(rowId));
}

export { admitBundleHeaders };
